/**
 * L5 dev-mode error reporter — 前端运行时错误 → 后端 /__dev/log 端点。
 *
 * 让 `make test-l5` 单流终端能同时看到前端 + 后端错误,无需切浏览器 DevTools。
 *
 * 触发源:
 *   1. window.addEventListener('error')         — JS runtime + 资源加载错误
 *   2. window.addEventListener('unhandledrejection') — Promise reject 未处理
 *   3. console.error / console.warn 包装       — 应用层日志(包含 React 异常)
 *
 * 生产构建(`import.meta.env.PROD`)整段 dead-code 消除,0 运行时开销。
 *
 * 后端必须 DOGDISK_DEV_MODE=true 才挂载 /__dev/log handler;否则 POST 静默丢弃。
 */

type ReportLevel = 'error' | 'warn'

type ReportPayload = {
  spa: string
  level: ReportLevel
  msg: string
  stack?: string
  url: string
  user_agent: string
  ts: string
}

const QUEUE_MAX = 50
const FLUSH_INTERVAL_MS = 2000
const queue: ReportPayload[] = []
let installed = false
let originalConsoleError: typeof console.error | null = null
let originalConsoleWarn: typeof console.warn | null = null

function detectSpa(): string {
  // 端口约定:8081 user / 8082 admin / 8083 service / 8084 agent
  if (typeof window === 'undefined') return 'unknown'
  const port = window.location.port
  return (
    {
      '8081': 'user',
      '8082': 'admin',
      '8083': 'service',
      '8084': 'agent',
    }[port] ?? 'unknown'
  )
}

function enqueue(level: ReportLevel, msg: string, stack?: string) {
  if (queue.length >= QUEUE_MAX) return
  queue.push({
    spa: detectSpa(),
    level,
    msg: msg.slice(0, 2000),
    ...(stack ? { stack: stack.slice(0, 4000) } : {}),
    url: window.location.href,
    user_agent: navigator.userAgent,
    ts: new Date().toISOString(),
  })
}

async function flush() {
  if (queue.length === 0) return
  const batch = queue.splice(0, queue.length)
  try {
    await fetch('/__dev/log', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ events: batch }),
      keepalive: true,
    })
  } catch {
    // 静默 — 后端可能未启 DEV 模式,不影响业务
  }
}

function stringifyArg(arg: unknown): string {
  if (arg instanceof Error) return arg.stack ?? arg.message
  if (typeof arg === 'string') return arg
  try {
    return JSON.stringify(arg)
  } catch {
    return String(arg)
  }
}

/**
 * installDevErrorReporter 安装全局 hook。幂等 — 重复调用 no-op。
 * 仅在 Vite DEV 模式生效(生产构建 tree-shake 掉)。
 */
export function installDevErrorReporter() {
  if (installed) return
  installed = true

  window.addEventListener('error', (e: ErrorEvent) => {
    const errMsg = e.error instanceof Error ? e.error.message : e.message
    const stack = e.error instanceof Error ? e.error.stack : undefined
    enqueue('error', `[window.error] ${errMsg}`, stack)
  })

  window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
    const reason = e.reason
    const msg = reason instanceof Error ? reason.message : String(reason)
    const stack = reason instanceof Error ? reason.stack : undefined
    enqueue('error', `[unhandledrejection] ${msg}`, stack)
  })

  originalConsoleError = console.error.bind(console)
  console.error = (...args: unknown[]) => {
    originalConsoleError?.(...args)
    enqueue('error', `[console.error] ${args.map(stringifyArg).join(' ')}`)
  }

  originalConsoleWarn = console.warn.bind(console)
  console.warn = (...args: unknown[]) => {
    originalConsoleWarn?.(...args)
    enqueue('warn', `[console.warn] ${args.map(stringifyArg).join(' ')}`)
  }

  setInterval(flush, FLUSH_INTERVAL_MS)
  window.addEventListener('beforeunload', () => void flush())
}
